/**
 * Excel «Отчёт по операциям»: та же структура, что Talend tFileOutputExcel_1
 * (порядок A–P, лист «Отчет по операциям», числа I–N).
 * promts/revert 2nd tab.md §3, columns_in_excel.md
 */

import XLSX from 'xlsx';
import {
  formatDateDdMmYyyy,
  OperationReportApiOperation,
  roundLikeTalend,
} from '@/utils/operationReportDisplay';

/**
 * Ширины колонок после Autofit в Excel (OOXML `width`)
 * Индекс 0 = колонка A.
 * SheetJS: `wch` — близкая к единицам Excel ширина в «символах».
 */
export const OPERATION_REPORT_EXCEL_COL_WIDTHS_OOXML = [
  9.52, 11.1, 17.07, 15.76, 9.78, 6.71, 81.82, 25.16,
  7.15, 11.8, 6.1, 10.75, 11.28, 15.58, 10.75, 35.26,
] as const;

/** Заголовки строки 1 — как в Talend (A–P) */
export const OPERATION_REPORT_EXCEL_HEADERS = [
  '№ Заявки',
  'Платформа',
  'Поставщик',
  'Клиент',
  'Дата',
  'Смена',
  'Тип операции',
  'Тип биллинга',
  'Объём',
  'Объём Факт',
  'Люди',
  'Люди Факт',
  'Время Факт',
  'Тариф операции',
  'Кост-центр',
  'Статус',
] as const;

const SHEET_NAME = 'Отчет по операциям';

/** Индексы колонок I–N (0-based): числовые ячейки в Excel */
const NUM_COL_START = 8;
const NUM_COL_END = 13;

function metricNumber(v: unknown): number {
  const r = roundLikeTalend(v);
  return r === null ? 0 : r;
}

function tariffNumberOrEmpty(v: unknown): number | null {
  const r = roundLikeTalend(v);
  if (r === null || r === 0) return null;
  return r;
}

function operationToExcelRow(op: OperationReportApiOperation): (string | number | null)[] {
  const appId = op.application?.id;
  const colA = appId === null || appId === undefined || appId === '' ? '' : String(appId);

  return [
    colA,
    op.application?.platform?.name ?? '',
    op.application?.Supplier?.legal_name ?? '',
    op.application?.client?.legal_name ?? '',
    formatDateDdMmYyyy(op.date),
    op.time_interval === 'day' ? 'День' : 'Ночь',
    op.workType?.name ?? '',
    op.workType?.work_billing_type?.name ?? '',
    metricNumber(op.size),
    metricNumber(op.actual_size),
    metricNumber(op.quantity),
    metricNumber(op.actual_quantity),
    metricNumber(op.actual_work_time),
    tariffNumberOrEmpty(op.tariff),
    op.workType?.cost_center?.name ?? '',
    op.application?.status?.name ?? '',
  ];
}

/**
 * Строит книгу XLSX: один лист, порядок колонок A–P, типы n для I–N.
 */
export function buildOperationReportWorkbook(operations: OperationReportApiOperation[]): XLSX.WorkBook {
  const aoa: (string | number | null)[][] = [Array.from(OPERATION_REPORT_EXCEL_HEADERS)];
  operations.forEach((op) => {
    aoa.push(operationToExcelRow(op));
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws['!cols'] = OPERATION_REPORT_EXCEL_COL_WIDTHS_OOXML.map((w) => ({ wch: w }));

  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  for (let r = 1; r <= range.e.r; r += 1) {
    for (let c = NUM_COL_START; c <= NUM_COL_END; c += 1) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = ws[addr];
      if (!cell) continue;
      if (cell.v === null || cell.v === '') {
        delete ws[addr];
        continue;
      }
      if (typeof cell.v === 'number') {
        cell.t = 'n';
      }
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, SHEET_NAME);
  return wb;
}
